import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaSave, FaTimes, FaInfoCircle,
  FaTruck, FaMoneyBillWave, FaCalendarAlt,
  FaSpinner, FaGavel
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieBidForm({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tender, setTender] = useState(null);
  const [existingBid, setExistingBid] = useState(null);
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);

  const [formData, setFormData] = useState({
    bid_rate: '',
    trucks_offered: '',
    monthly_capacity_mt: '',
    availability_date: '',
    notes: ''
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchTenderAndBid(savedId);
        return;
      }

      const response = await fetch(`/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const subbieId = data.subcontractors[0].id;
        setSubbieId(subbieId);
        localStorage.setItem('subbieId', subbieId);
        fetchTenderAndBid(subbieId);
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchTenderAndBid = async (subbieId) => {
    setLoading(true);
    try {
      const [tenderRes, bidRes] = await Promise.all([
        fetch(`/tenders/${id}`),
        fetch(`/tenders/${id}/my-bid?subcontractor_id=${subbieId}`)
      ]);

      const tenderData = await tenderRes.json();
      const bidData = await bidRes.json();

      if (tenderData.success) {
        setTender(tenderData.tender);
      }

      if (bidData.success && bidData.bid) {
        setExistingBid(bidData.bid);
        setFormData({
          bid_rate: bidData.bid.bid_rate,
          trucks_offered: bidData.bid.trucks_offered,
          monthly_capacity_mt: bidData.bid.monthly_capacity_mt || '',
          availability_date: bidData.bid.availability_date || '',
          notes: bidData.bid.notes || ''
        });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!formData.bid_rate || !formData.trucks_offered) {
      toast.error('Please fill in all required fields');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`/tenders/${id}/bid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcontractor_id: subbieId,
          ...formData,
          bid_rate: parseFloat(formData.bid_rate),
          trucks_offered: parseInt(formData.trucks_offered),
          monthly_capacity_mt: formData.monthly_capacity_mt ? parseFloat(formData.monthly_capacity_mt) : null,
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(existingBid ? '✅ Bid updated successfully!' : '✅ Bid submitted successfully!');
        navigate('/subbie-tenders');
      } else {
        toast.error(data.error || 'Failed to submit bid');
      }
    } catch (err) {
      console.error('Error submitting bid:', err);
      toast.error('Failed to submit bid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/subbie-tenders');
  };

  const isExpired = tender && new Date(tender.bidding_deadline) < new Date();

  if (loading) {
    return (
      <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (!tender) {
    return (
      <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Tender Not Found</h2>
          <p className="text-gray-500">The tender you're looking for doesn't exist.</p>
          <Link to="/subbie-tenders" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Tender Board
          </Link>
        </div>
      </SubbieLayout>
    );
  }

  if (isExpired) {
    return (
      <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">⏰</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Tender Expired</h2>
          <p className="text-gray-500">The bidding deadline has passed for this tender.</p>
          <Link to="/subbie-tenders" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Tender Board
          </Link>
        </div>
      </SubbieLayout>
    );
  }

  const job = tender.jobs;

  return (
    <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={handleCancel}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: '#0A1929' }}
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaGavel className="inline mr-2" style={{ color: '#00A86B' }} />
            {existingBid ? 'Update Bid' : 'Submit Bid'}
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {tender.tender_number} - {job?.commodity || 'Unknown'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        {/* Tender Summary */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Tender</p>
              <p className="font-medium">{tender.tender_number}</p>
            </div>
            <div>
              <p className="text-gray-500">Type</p>
              <p className="font-medium capitalize">{tender.tender_type}</p>
            </div>
            <div>
              <p className="text-gray-500">Quantity</p>
              <p className="font-medium">{tender.total_volume_mt} MT</p>
            </div>
            <div>
              <p className="text-gray-500">Deadline</p>
              <p className="font-medium">{new Date(tender.bidding_deadline).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bid Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Rate (R/MT) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="bid_rate"
                value={formData.bid_rate}
                onChange={handleChange}
                placeholder="e.g. 450"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
              />
              {tender.target_rate && (
                <p className="text-xs text-gray-400 mt-1">Target rate: R{tender.target_rate}/MT</p>
              )}
            </div>

            {/* Trucks Offered */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trucks You Can Offer <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="trucks_offered"
                value={formData.trucks_offered}
                onChange={handleChange}
                placeholder="e.g. 5"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
              />
            </div>

            {/* Monthly Capacity (Framework only) */}
            {tender.tender_type === 'framework' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Capacity (MT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="monthly_capacity_mt"
                  value={formData.monthly_capacity_mt}
                  onChange={handleChange}
                  placeholder="e.g. 2000"
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                />
                <p className="text-xs text-gray-400 mt-1">Required for framework tenders</p>
              </div>
            )}

            {/* Availability Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Availability Date
              </label>
              <input
                type="date"
                name="availability_date"
                value={formData.availability_date}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Any additional information for the admin..."
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <FaInfoCircle className="text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">What happens next?</p>
                <p className="text-sm text-blue-700 mt-1">
                  {existingBid 
                    ? 'Your bid will be updated. The admin will review all bids after the deadline.' 
                    : 'Your bid will be submitted. You can update it before the deadline.'}
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#00A86B' }}
            >
              <FaSave size={14} />
              {submitting ? 'Submitting...' : (existingBid ? 'Update Bid' : 'Submit Bid')}
            </button>
          </div>
        </form>
      </div>
    </SubbieLayout>
  );
}

export default SubbieBidForm;
